import { useEffect, useState } from 'react';

import { useDayPriceHistory } from '@hooks/useDayPriceHistory';
import { usePrice } from '@hooks/usePrice';
import { useRealtimePrices } from '@hooks/useRealtimePrices';
import type { Asset } from '@shared/types';

import {
  fillPriceHistoryGaps,
  generateDynamicStrikePrices,
} from '../../utils/chartHelpers';

interface UseMarketDataReturn {
  // Core data
  spotPrice: number;
  strikePrices: number[];
  selectedStrikePrice: number;
  priceHistory: Array<{ price: number; timestamp: number }>;

  // Loading states
  isLoading: boolean;
  isReady: boolean;

  // Actions
  selectStrike: (price: number) => void;
  deselectStrike: () => void;
}

export function useMarketData(asset: Asset): UseMarketDataReturn {
  const realtimePricesQuery = useRealtimePrices(asset);
  const priceQuery = usePrice(asset);

  const [selectedStrikePrice, setSelectedStrikePrice] = useState(0);
  const [strikePrices, setStrikePrices] = useState<number[]>([]);
  const [hasInitializedStrikes, setHasInitializedStrikes] = useState(false);
  const [recoveryState, setRecoveryState] = useState<
    'idle' | 'needed' | 'in_progress' | 'completed'
  >('idle');

  // Get real-time data from WebSocket
  const realtimeData = realtimePricesQuery.data ?? [];

  // Process raw data and detect stale data
  const rawData = realtimeData;

  // Apply gap filling ONLY to real-time data, not to the combined dataset
  // The minute candle data should remain smooth (no gap filling)
  const filledRealtimeData = fillPriceHistoryGaps(rawData);

  // Get day history with filled real-time data
  const dayHistoryWithFilledRealtime = useDayPriceHistory({
    asset,
    realtimeData: filledRealtimeData,
  });

  const priceHistory = dayHistoryWithFilledRealtime.dayPriceHistory;
  const spotPrice = priceQuery.data?.price ?? 0;

  // Loading state
  const isLoading =
    realtimePricesQuery.isLoading ||
    priceQuery.isLoading ||
    dayHistoryWithFilledRealtime.isLoading ||
    recoveryState === 'in_progress';

  // Ready state - all essential data is available
  const isReady =
    spotPrice > 0 &&
    strikePrices.length > 0 &&
    priceHistory.length >= 10 &&
    !isLoading;

  // Reset when asset changes
  useEffect(() => {
    setStrikePrices([]);
    setSelectedStrikePrice(0);
    setHasInitializedStrikes(false);
    setRecoveryState('idle');
  }, [asset]);

  // Initialize strikes when we have valid spot price
  useEffect(() => {
    if (spotPrice > 0 && !hasInitializedStrikes) {
      const newStrikePrices = generateDynamicStrikePrices(spotPrice, asset);

      setStrikePrices(newStrikePrices);
      setHasInitializedStrikes(true);
    }
  }, [spotPrice, asset, hasInitializedStrikes]);

  // Action methods for strike selection
  const selectStrike = (price: number) => {
    if (strikePrices.includes(price)) {
      setSelectedStrikePrice(price);
    }
  };

  const deselectStrike = () => {
    setSelectedStrikePrice(0);
  };

  return {
    // Core data
    spotPrice,
    strikePrices,
    selectedStrikePrice,
    priceHistory,

    // Loading states
    isLoading,
    isReady,

    // Actions
    selectStrike,
    deselectStrike,
  };
}
